import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import { formatLocaleCurrency } from "@/utils/formatUtils";
import { isDefined } from "@/utils/utils";
import { Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { PageSizes, TableSizes } from "../../../../../common/constants";
import { Permission } from "../../../../../common/security/enums";
import { useHasPermissions } from "../../../../auth/hooks";
import { CommissionBatchName } from "../../../components/CommissionBatchName";
import { CommissionAdvance, CommissionAdvanceCycle } from "../../types";
import { CommissionAdvanceStatusTag } from "../CommissionAdvanceStatusTag";

interface Props {
  advance: CommissionAdvance;
}

export const CommissionAdvanceCyclesTable = ({ advance }: Props) => {
  const { t } = useTranslation();

  const hasCommissionsPermission = useHasPermissions(Permission.COMMISSIONS);

  const columns = useMemo(() => {
    const paidColumns: ColumnsType<CommissionAdvanceCycle> = [
      {
        key: "paidFeePaymentsCount",
        title: t("commissions.advance.helpers.cyclesTable.feePaymentsCount"),
        align: "right",
        width: 80,
        render: (_, record) => record.paidFeePaymentsCount,
      },
      {
        key: "paidFeePaymentsIncrement",
        title: t("commissions.advance.helpers.cyclesTable.feePaymentsIncrement"),
        align: "right",
        width: 90,
        render: (_, record) => `+${record.paidFeePaymentsIncrement}`,
      },
      {
        key: "paidFeeAmount",
        title: t("commissions.advance.helpers.cyclesTable.feeAmount"),
        align: "right",
        width: 120,
        render: (_, record) => formatLocaleCurrency(record.paidFeePaymentsCount * advance.feePaymentAmount),
      },
    ];

    if (hasCommissionsPermission) {
      paidColumns.push({
        key: "surplusAmount",
        title: t("commissions.advance.attrs.cycles.surplusAmount"),
        align: "right",
        width: 120,
        render: (_, record) =>
          isDefined(record.surplusAmount) ? formatLocaleCurrency(record.surplusAmount) : undefined,
      });
    }

    const items: ColumnsType<CommissionAdvanceCycle> = [
      {
        key: "batch",
        title: t("commissions.advance.attrs.cycles.batch"),
        width: hasCommissionsPermission ? 250 : 510,
        ellipsis: { showTitle: false },
        render: (_, record) => (
          <CommissionBatchName
            batchName={record.batch.name}
            batchId={record.batch.id}
            showLink={hasCommissionsPermission}
          />
        ),
      },
      {
        key: "due",
        title: t("commissions.advance.helpers.cyclesTable.due"),
        children: [
          {
            key: "dueFeePaymentsCount",
            title: t("commissions.advance.helpers.cyclesTable.feePaymentsCount"),
            align: "right",
            width: 80,
            render: (_, record) => record.dueFeePaymentsCount,
          },
          {
            key: "dueFeeAmount",
            title: t("commissions.advance.helpers.cyclesTable.feeAmount"),
            align: "right",
            width: 120,
            render: (_, record) => formatLocaleCurrency(record.dueFeePaymentsCount * advance.feePaymentAmount),
          },
        ],
      },
      {
        key: "paid",
        title: t("commissions.advance.helpers.cyclesTable.paid"),
        children: paidColumns,
      },
    ];

    if (hasCommissionsPermission) {
      items.push({
        key: "receivedCommissionAmount",
        title: t("commissions.advance.attrs.cycles.receivedCommissionAmount"),
        align: "right",
        width: 140,
        render: (_, record) =>
          isDefined(record.receivedCommissionAmount)
            ? formatLocaleCurrency(record.receivedCommissionAmount)
            : undefined,
      });
    }

    items.push({
      key: "status",
      title: t("commissions.advance.attrs.cycles.status"),
      align: "center",
      width: 150,
      render: (_, record) => <CommissionAdvanceStatusTag status={record.status} />,
    });

    return items;
  }, [advance.feePaymentAmount, hasCommissionsPermission, t]);

  return (
    <Table<CommissionAdvanceCycle>
      {...tableStandardProps()}
      columns={columns}
      dataSource={advance.cycles}
      scroll={{ x: TableSizes.LARGE }}
      pagination={{
        ...paginationTableProps,
        placement: ["bottomEnd"],
        pageSize: PageSizes.TINY,
        total: advance.cycles.length,
      }}
    />
  );
};
